import type {ReactElement} from 'react';
import {useContext, useEffect, useRef} from 'react';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import type {EmailMakerBannerOptions} from '@EmailMakerReact/grapesjs-email-maker-plugin/src/email-maker-plugin.types';
import {EditorViewport} from '@EmailMakerReact/email-maker-react.types';
import type {EmailMakerCanvasDevice} from '@EmailMakerReact/email-maker-react.types';
import {setDefaultSelection} from '@EmailMakerReact/lib/email-maker-selection-library';
import {BannerStrip} from './components/banner-strip/banner-strip';
import {CanvasLoadingSkeleton} from './components/canvas-loading-skeleton/canvas-loading-skeleton';
import {DeviceSwitcher} from './components/device-switcher/device-switcher';
import {ZoomControls} from './components/zoom-controls/zoom-controls';
import type {EmailMakerCanvasProps} from './email-maker-canvas.types';
import styles from './email-maker-canvas.module.scss';

export function EmailMakerCanvas({onContainerReady}: EmailMakerCanvasProps): ReactElement {
  const {editor, state, actions} = useContext(emailMakerContext);
  const mountRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (mountRef.current) {
      onContainerReady(mountRef.current);
    }
  }, [onContainerReady]);
  useEffect(() => {
    const backdrop = backdropRef.current;
    if (!backdrop || !editor) {
      return undefined;
    }
    const onBackdropClick = (e: MouseEvent): void => {
      if (e.target === backdrop) {
        setDefaultSelection(editor);
      }
    };
    backdrop.addEventListener('click', onBackdropClick);
    return (): void => {
      backdrop.removeEventListener('click', onBackdropClick);
    };
  }, [editor]);
  return (
    <div className={styles.canvasArea} ref={backdropRef}>
      {renderBannerStrip(state.banner)}
      {renderDeviceSwitcherOnDesktopOnly(state.editorViewport, state.canvasDevice, actions.setCanvasDevice)}
      {renderZoomControlsOnDesktopOnly(state.editorViewport)}
      <div className={`${styles.canvasContainer} ${styles[state.canvasDevice] ?? ''}`}>
        <div className={`${styles.canvasMount} ${state.isEditorLoading ? styles.canvasMountHidden : ''}`} ref={mountRef} />
        {renderLoadingSkeleton(state.isEditorLoading)}
      </div>
    </div>
  );
}

function renderDeviceSwitcherOnDesktopOnly(
  editorViewport: EditorViewport,
  currentDevice: EmailMakerCanvasDevice,
  onSelect: (device: EmailMakerCanvasDevice) => void
): ReactElement | null {
  if (editorViewport !== EditorViewport.DESKTOP) {
    return null;
  }
  return <DeviceSwitcher currentDevice={currentDevice} onSelect={onSelect} />;
}

function renderZoomControlsOnDesktopOnly(editorViewport: EditorViewport): ReactElement | null {
  if (editorViewport !== EditorViewport.DESKTOP) {
    return null;
  }
  return <ZoomControls />;
}

function renderBannerStrip(banner: EmailMakerBannerOptions | null): ReactElement | null {
  if (banner === null) {
    return null;
  }
  return (
    <div className={styles.bannerContainer}>
      <BannerStrip message={banner.message} style={banner.style} icon={banner.icon} cta={banner.cta} ctaText={banner.ctaText} />
    </div>
  );
}

function renderLoadingSkeleton(isLoading: boolean): ReactElement | null {
  if (!isLoading) {
    return null;
  }
  return <CanvasLoadingSkeleton />;
}
